import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Group, Modal as MModal, NumberInput, Stack, Text, TextInput, Checkbox } from '@mantine/core';
import { motion } from 'framer-motion';
import { Building2, Plus } from 'lucide-react';
import { Badge, Button, Card, EmptyState, Table, type TableColumn } from '../components/ui';
import { PageContainer, PageHeader, Section } from '../components/ui';
import { cardItem, staggerContainer } from '../lib/motion';
import { ErrorState } from '../components/common/ErrorState';
import { LoadingState } from '../components/common/LoadingState';
import { useState } from 'react';
import { useCreateTenant, useInviteUser, useTenants, type AdminTenant } from '../hooks/useAdmin';
import { selectRole, useAuthStore } from '../store/useAuthStore';
import { toast } from '../store/useToasts';

export const Route = createFileRoute('/admin/tenants')({
  component: AdminTenants,
});

function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

export function AdminTenants({ basePath = '/admin' }: { basePath?: string } = {}) {
  const { data, isPending, isError, error, refetch } = useTenants();
  const role = useAuthStore(selectRole);
  const canCreate = role === 'superadmin'; // only the platform superadmin creates organizations
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [spendCap, setSpendCap] = useState<number | ''>(500);
  const [licenseMonths, setLicenseMonths] = useState<number | ''>(12);
  const [scenariosEnabled, setScenariosEnabled] = useState(true);
  const create = useCreateTenant();
  const invite = useInviteUser();
  const navigate = useNavigate();

  const submit = () => {
    if (!name.trim() || !adminEmail.trim()) {
      toast.error('Organization name and admin email are required');
      return;
    }
    create.mutate(
      { name: name.trim(), adminEmail: adminEmail.trim(),
        ...(spendCap === '' ? {} : { spendCap: Number(spendCap) }),
        ...(licenseMonths === '' ? {} : { licenseMonths: Number(licenseMonths) }),
        scenariosEnabled },
      {
        // The tenancy service only creates the org; the org admin is a separate
        // invite (Cognito emails them a temporary password). Without this the
        // admin email was silently dropped and nobody was ever invited.
        onSuccess: (org) => {
          const email = adminEmail.trim();
          setOpen(false); setName(''); setAdminEmail('');
          invite.mutate(
            { email, role: 'org_admin', tenantId: String(org.id) },
            {
              onSuccess: () => toast.success(`Organization created. Invite emailed to ${email}`),
              onError: (e) => toast.error(
                `Organization created, but inviting ${email} failed: ${e.message || 'unknown error'}. `
                + 'Invite them from Users.'),
            },
          );
        },
        onError: (e) => toast.error(e.message || 'Could not create the organization'),
      },
    );
  };

  const columns: ReadonlyArray<TableColumn<AdminTenant>> = [
    { key: 'name', header: 'Tenant', cell: (t) => (
      <Stack gap={2}>
        <Text size="sm" c="rgb(var(--c-txt-bright))">{t.name}</Text>
        <Text size="xs" c={t.licenseValid === false ? 'red' : 'dimmed'} className="font-mono">
          {t.licenseExpiresAt
            ? `License ${fmtDate(t.licenseStartedAt)} \u2192 ${fmtDate(t.licenseExpiresAt)}${t.licenseValid === false ? ' (expired)' : ''}`
            : 'License: no expiry'}
        </Text>
      </Stack>
    ) },
    { key: 'id', header: 'ID', cell: (t) => <Text size="xs" c="dimmed" className="font-mono" title={t.id}>{String(t.id).slice(0, 8)}</Text> },
    {
      key: 'status',
      header: 'Status',
      cell: (t) => (
        <Badge variant={t.status === 'active' ? 'success' : t.status === 'trial' ? 'info' : 'danger'} size="xs">
          {t.status}
        </Badge>
      ),
    },
    { key: 'users', header: 'Users', align: 'right', cell: (t) => <Text size="sm">{t.users}</Text> },
    { key: 'ranges', header: 'Ranges', align: 'right', cell: (t) => <Text size="sm">{t.ranges}</Text> },
    {
      key: 'spend',
      header: 'MTD / Cap',
      align: 'right',
      cell: (t) => (
        <Text size="sm" className="font-mono">
          ${t.monthToDate.toFixed(2)} / ${t.spendCap}
        </Text>
      ),
    },
  ];

  return (
    <PageContainer size="wide">
      <PageHeader
        icon={Building2}
        title="Tenants"
        description="Organizations using Forge and their resource limits."
        breadcrumbs={[{ label: 'Forge', to: '/' }, { label: 'Admin', to: '/admin' }, { label: 'Tenants' }]}
        actions={
          canCreate ? (
            <Button variant="primary" leftIcon={<Plus size={14} />} onClick={() => setOpen(true)}>
              New organization
            </Button>
          ) : undefined
        }
      />

      {isError ? (
        <ErrorState
          title="Could not load tenants"
          message={(error as Error)?.message ?? 'Unknown error'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <LoadingState rows={4} height={32} />
      ) : data && data.length === 0 ? (
        <EmptyState
          illustration={<Building2 size={48} />}
          title="No tenants yet"
          description="Once your first organization onboards, it will appear here."
          {...(canCreate ? { cta: { label: 'New organization', onClick: () => setOpen(true) } } : {})}
        />
      ) : (
        <Section
          title={`All tenants (${data?.length ?? 0})`}
          description="Suspending a tenant freezes new deploys without interrupting running sessions."
        >
          <motion.div variants={staggerContainer} initial="hidden" animate="show">
            <motion.div variants={cardItem}>
              <Card>
                <Stack gap="md">
                  <Group gap="xs">
                    <Building2 size={16} />
                    <Text fw={600} size="sm">
                      All tenants ({data?.length ?? 0})
                    </Text>
                  </Group>
                  <Table columns={columns} rows={data ?? []} rowKey={(t) => t.id}
                    {...(basePath === '/admin' ? { onRowClick: (t: AdminTenant) => void navigate({ to: '/admin/tenants/$tenantId', params: { tenantId: t.id } }) } : {})}
                    rowLabel={(t) => `Open ${t.name}`} />
                </Stack>
              </Card>
            </motion.div>
          </motion.div>
        </Section>
      )}

      <MModal opened={open} onClose={() => setOpen(false)} title="New organization" centered>
        <Stack gap="md">
          <TextInput label="Organization name" placeholder="Acme University" value={name}
            onChange={(e) => setName(e.currentTarget.value)} required data-autofocus />
          <TextInput label="Admin email" placeholder="admin@acme.edu" type="email" value={adminEmail}
            onChange={(e) => setAdminEmail(e.currentTarget.value.trim())} required
            description="An org-admin invite is sent to this address; they manage users + see this org's cost." />
          <NumberInput label="Monthly spend cap (USD)" value={spendCap} min={0}
            onChange={(v) => setSpendCap(typeof v === 'number' ? v : '')} />
          <NumberInput label="License duration (months)" value={licenseMonths} min={1} max={120}
            description="Sets the license expiry. The org cannot use the platform after it lapses."
            onChange={(v) => setLicenseMonths(typeof v === 'number' ? v : '')} />
          <Checkbox label="Scenarios enabled (commercial add-on)" checked={scenariosEnabled}
            description="When off, the org sees the Scenarios tab but cannot deploy — a support prompt is shown instead."
            onChange={(e) => setScenariosEnabled(e.currentTarget.checked)} />
          <Group justify="flex-end" gap="sm">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={create.isPending}>Cancel</Button>
            <Button variant="primary" onClick={submit} loading={create.isPending || invite.isPending}>Create organization</Button>
          </Group>
        </Stack>
      </MModal>
    </PageContainer>
  );
}
